// Reusable analytics primitives.
import { useMemo } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const RON = (v, { decimals = 0 } = {}) => {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("ro-RO", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const fmtMoney = (v, d = 2) => RON(v, { decimals: d });
export const fmtInt = (v) => v == null ? "—" : Number(v).toLocaleString("ro-RO");
export const fmtPct = (v, d = 1) => v == null ? "—" : `${Number(v).toFixed(d)}%`;

export function Sparkline({ data = [], color = "#E91E63", height = 28, width = 90 }) {
  const { path, area } = useMemo(() => {
    if (!data.length) return { path: "", area: "" };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = width / Math.max(1, data.length - 1);
    const pts = data.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return [x, y];
    });
    const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${path} L${width},${height} L0,${height} Z`;
    return { path, area };
  }, [data, width, height]);
  if (!data.length) return null;
  const id = `spark-grad-${color.replace("#", "")}`;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function KpiCard({ label, value, delta, format = "number", spark, color = "#E91E63", icon: Icon, suffix, testId, prefix }) {
  const fmt = (v) => {
    if (v == null) return "—";
    if (format === "money") return fmtMoney(v, 2) + " RON";
    if (format === "pct") return fmtPct(v);
    if (format === "ratio") return Number(v).toFixed(2) + "×";
    return fmtInt(v);
  };
  const positiveIsGood = delta?.kind !== "inverse";
  const goingUp = (delta?.value ?? 0) > 0;
  const trendColor = delta == null || delta.value == null ? "text-muted-foreground" :
    (goingUp === positiveIsGood ? "text-emerald-500" : "text-rose-500");
  const TrendIcon = delta == null || delta.value == null ? Minus : (goingUp ? TrendingUp : TrendingDown);
  return (
    <div
      data-testid={testId}
      className="rounded-xl bg-card/70 border border-border p-4 flex flex-col gap-2 hover:border-primary/40 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          {Icon && <Icon className="h-3 w-3" />}
          {label}
        </div>
        {spark && <Sparkline data={spark} color={color} />}
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-display font-semibold tabular-nums tracking-tight">
          {prefix}{fmt(value)}{suffix ? <span className="text-sm text-muted-foreground font-normal ml-1">{suffix}</span> : null}
        </div>
      </div>
      {delta && delta.value != null && (
        <div className={`text-xs flex items-center gap-1 ${trendColor}`}>
          <TrendIcon className="h-3 w-3" />
          {Math.abs(delta.value).toFixed(1)}%
          <span className="text-muted-foreground ml-1">{delta.label || "vs. perioada anterioară"}</span>
        </div>
      )}
    </div>
  );
}
